import Module from '@/store/resource/common/module';
import Org from '@/store/resource/org';
import api from '@/factories/api';

let module = new Module(new Org());

module.add('state', {
  usageLimitsLoaded: false,
  usageLimits: null,
  orgBilling: null,
  orgSettings: null,
  invoices: [],
});

module.add('actions', {
  async loadOrgSettings({ commit, state }, force = false) {
    if (state.orgSettings && !force) {
      return state.orgSettings;
    }

    const res = await api().get('org');
    commit('setOrgSettings', res);
    return res;
  },

  async updateOrgSettings({ dispatch }, data) {
    const res = await api().put('org', data);
    const { public_key } = res || {};
    if (public_key) {
      this.commit('org/updateOrgSettings', {
        public_encryption_key: public_key,
      });
    }

    dispatch('loadOrgSettings', true);
    return res;
  },

  // Sudo-specific actions for backend settings (support users only)
  // These do NOT update shared orgSettings state to avoid conflicts
  async loadOrgSettingsSudo() {
    return await api().get('org?sudo=1');
  },

  async updateOrgSettingsSudo(_, data) {
    return await api().put('org?sudo=1', data);
  },

  async loadUsageLimits({ commit }) {
    const res = await api().get(`org/usage`);
    commit('setUsageLimits', res);
    return res;
  },

  async refreshUsageLimit({ commit, state }, { type }) {
    const limit = state.usageLimits?.[type];
    if (!limit || limit.cap <= 0 || limit.cap >= 1000000) return;

    const data = await api().get(`org/usage?type=${type}`);
    const updated = data?.[type];
    if (!updated) return;

    commit('updateUsageLimit', { type, limit: updated });
  },

  async getOrgBilling({ commit }, shareToken) {
    commit('loading');
    try {
      const billing = await api({ shareToken }).get('org/billing');
      commit('setOrgBilling', billing);
    } finally {
      commit('loading', false);
    }
  },

  // Consolidated billing action handler (used by AccountStripeModal)
  async updateOrgBilling({}, { params, shareToken } = {}) {
    return await api({ shareToken }).put('org/billing', params);
  },

  async notifyAboutBilling(_, user_ids) {
    if (!user_ids.length) return;

    await api().post('org/billing_notify', { user_ids });
  },

  async updateOrgPlan({ commit }, data) {
    return await api().put('org/plan', data);
  },

  async getOrgInvoices({ commit }, shareToken) {
    try {
      const data = await api({ shareToken }).get('org/invoices');
      commit('setOrgInvoice', data?.invoices || []);
    } catch (err) {
      throw err;
    }
  },
});

module.add('getters', {
  orgPublicKey(state) {
    return state.orgSettings?.public_encryption_key;
  },
  fieldSections(state) {
    const sections = state.orgSettings?.meta?.field_sections || [];
    return [
      {
        title: 'Default',
        slug: '',
        locked: true,
      },
      ...sections,
    ];
  },
  campaignSections(state) {
    const sections = state.orgSettings?.meta?.portal_sections || [];
    return [
      {
        title: 'No Portal Section',
        slug: '',
        id: null,
        locked: true,
      },
      ...sections.map((section) => ({
        ...section,
        // Use id if available (new backend), otherwise use slug (old backend)
        id: section.id || section.slug,
      })),
    ];
  },
  isLimitLocked: (state) => (limitKey) => {
    return state.usageLimits?.[limitKey]?.cap === 0;
  },
  isLimitReached: (state) => (limitKey) => {
    const limit = state.usageLimits?.[limitKey];
    if (!limit) return false;
    const { cap, used } = limit;
    if (cap === -1) return false;
    return cap === 0 || used >= cap;
  },
});

module.add('mutations', {
  setUsageLimits(state, data) {
    state.usageLimits = data;
    state.usageLimitsLoaded = true;
  },
  updateUsageLimit(state, { type, limit }) {
    if (!state.usageLimits) return;
    state.usageLimits = { ...state.usageLimits, [type]: limit };
  },
  setOrgBilling(state, data) {
    state.orgBilling = data;
  },
  setOrgInvoice(state, data) {
    state.invoices = data;
  },
  setOrgSettings(state, data) {
    state.orgSettings = { ...state.orgSettings, ...data };
  },
});

export default module.create();
